import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import api, { formatApiErrorDetail } from "./adminApi";
import { ImageUpload, MultiImageUpload } from "./ImageUpload";
import { imgUrl } from "../lib/spa";
import {
  NATIONALITIES,
  HAIR,
  EYES,
  expandFromCatalog,
  isInCatalog,
} from "../lib/therapistAttributeCatalog";

const inp =
  "w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-gold";
const ta = `${inp} min-h-[110px] resize-y leading-relaxed`;
const smallInp = `${inp} py-2`;

const emptyLocalized = () => ({ pt: "", en: "", es: "" });

const initialForm = (therapist, duplicateFrom) => {
  const source = therapist || duplicateFrom || null;
  if (!source) {
    return {
      name: "",
      order: 99,
      age: "",
      nationality: emptyLocalized(),
      hair: emptyLocalized(),
      eyes: emptyLocalized(),
      weight: "",
      height: "",
      about: emptyLocalized(),
      massages: [],
      profile: "",
      gallery: [],
      available_today: false,
      active: true,
    };
  }
  return {
    // When duplicating, blank out the name so the operator has to type a new one
    name: therapist ? source.name : "",
    order: source.order ?? 99,
    age: source.age ?? "",
    nationality: { ...emptyLocalized(), ...(source.nationality || {}) },
    hair: { ...emptyLocalized(), ...(source.hair || {}) },
    eyes: { ...emptyLocalized(), ...(source.eyes || {}) },
    weight: source.weight || "",
    height: source.height || "",
    about: { ...emptyLocalized(), ...(source.about || {}) },
    massages: [...(source.massages || [])],
    // On duplicate, keep the photos as a starting point — user may replace them
    profile: source.profile || "",
    gallery: [...(source.gallery || [])],
    available_today: !!source.available_today,
    active: source.active !== false,
  };
};

const STEPS = [
  { key: "info", label: "Informação" },
  { key: "photos", label: "Fotos" },
  { key: "massages", label: "Massagens & Preview" },
];

// Small preview matching how the card looks on the /terapeutas page
const CardPreview = ({ form, massages }) => {
  const selectedMassages = massages
    .filter((m) => form.massages.includes(m.slug))
    .slice(0, 3)
    .map((m) => (m.name?.pt || m.slug).replace(/^Massagem\s+/i, ""));

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-black">
      <div className="relative aspect-[3/5] w-full bg-black/40">
        {form.profile ? (
          <img
            src={imgUrl(form.profile)}
            alt={form.name || "preview"}
            className="h-full w-full object-cover opacity-90"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-white/25">
            Sem foto
          </div>
        )}
        {form.available_today && (
          <span className="absolute left-3 top-3 rounded-full bg-gold/95 px-3 py-1 text-[10px] font-medium uppercase tracking-widest text-black">
            Disponível Hoje
          </span>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black via-black/70 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-4">
          <h3 className="font-display text-xl font-medium text-white">
            {form.name || "Nome da terapeuta"}
          </h3>
          <p className="mt-1 text-xs text-white/60">
            {selectedMassages.length > 0
              ? selectedMassages.join(" • ")
              : "Sem massagens atribuídas"}
          </p>
        </div>
      </div>
    </div>
  );
};

export const TherapistForm = ({ therapist, duplicateFrom, massages, onClose, onSaved }) => {
  const [form, setForm] = useState(() => initialForm(therapist, duplicateFrom));
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLocalized = (field, lang, v) =>
    setForm((f) => ({ ...f, [field]: { ...f[field], [lang]: v } }));
  // Catalog-managed fields: setting PT auto-expands EN/ES from the catalog.
  const CATALOG_BY_FIELD = { nationality: NATIONALITIES, hair: HAIR, eyes: EYES };
  const setCatalog = (field, ptValue) => {
    const catalog = CATALOG_BY_FIELD[field];
    setForm((f) => ({
      ...f,
      [field]: expandFromCatalog(catalog, { ...f[field], pt: ptValue }),
    }));
  };
  const toggleMassage = (slug) =>
    set(
      "massages",
      form.massages.includes(slug)
        ? form.massages.filter((s) => s !== slug)
        : [...form.massages, slug]
    );

  const isEdit = !!therapist;
  const title = isEdit
    ? `Editar ${therapist.name}`
    : duplicateFrom
      ? `Duplicar de ${duplicateFrom.name}`
      : "Nova Terapeuta";

  const canProceed = useMemo(() => {
    if (step === 0) return !!form.name.trim();
    return true;
  }, [step, form.name]);

  const save = async () => {
    if (!form.name.trim()) {
      setStep(0);
      toast.error("Preencha o nome antes de guardar");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 99,
        age: form.age === "" || form.age === null ? null : Number(form.age),
      };
      if (isEdit) {
        await api.put(`/admin/therapists/${therapist.slug}`, payload);
      } else {
        await api.post("/admin/therapists", payload);
      }
      toast.success("Terapeuta guardada com sucesso");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="therapist-form"
        aria-describedby={undefined}
        className="max-h-[92vh] max-w-4xl overflow-y-auto border-white/10 bg-[#101010] text-white"
      >
        <DialogTitle className="font-display text-2xl font-medium">{title}</DialogTitle>

        {/* Stepper */}
        <div className="mt-1 flex items-center gap-2">
          {STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            return (
              <button
                key={s.key}
                type="button"
                data-testid={`therapist-step-${s.key}`}
                onClick={() => setStep(i)}
                className={`flex flex-1 items-center gap-2 rounded-full px-3 py-2 text-xs uppercase tracking-widest transition-colors ${
                  active
                    ? "bg-gold/10 text-gold"
                    : done
                      ? "text-white/70 hover:text-gold"
                      : "text-white/30 hover:text-white/60"
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${
                    active ? "bg-gold text-black" : done ? "bg-gold/40 text-black" : "bg-white/10 text-white/50"
                  }`}
                >
                  {done ? <Check className="h-3 w-3" strokeWidth={2.5} /> : i + 1}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-6 space-y-5">
          {/* ─────────── STEP 1 — Info ─────────── */}
          {step === 0 && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">
                    Nome *
                  </label>
                  <input
                    data-testid="therapist-name-input"
                    className={inp}
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">
                    Ordem
                  </label>
                  <input
                    data-testid="therapist-order-input"
                    type="number"
                    className={inp}
                    value={form.order}
                    onChange={(e) => set("order", e.target.value)}
                  />
                </div>
              </div>

              {/* Structured attributes */}
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <p className="mb-3 text-[10px] uppercase tracking-[0.3em] text-gold">
                  Atributos estruturados
                </p>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-[11px] uppercase tracking-widest text-white/40">
                      Idade
                    </label>
                    <input
                      data-testid="therapist-age-input"
                      type="number"
                      min="18"
                      max="99"
                      className={smallInp}
                      value={form.age}
                      onChange={(e) => set("age", e.target.value)}
                      placeholder="27"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] uppercase tracking-widest text-white/40">
                      Peso
                    </label>
                    <input
                      data-testid="therapist-weight-input"
                      className={smallInp}
                      value={form.weight}
                      onChange={(e) => set("weight", e.target.value)}
                      placeholder="55kg"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] uppercase tracking-widest text-white/40">
                      Altura
                    </label>
                    <input
                      data-testid="therapist-height-input"
                      className={smallInp}
                      value={form.height}
                      onChange={(e) => set("height", e.target.value)}
                      placeholder="1,60"
                    />
                  </div>
                </div>

                {/* Catalog-driven: nationality / hair / eyes — só se escolhe em PT, EN/ES aut. */}
                <CatalogSelectRow
                  label="Nacionalidade"
                  field="nationality"
                  value={form.nationality}
                  catalog={NATIONALITIES}
                  onChange={setCatalog}
                  onCustomChange={setLocalized}
                  placeholder="Selecionar nacionalidade…"
                  testId="therapist-nationality"
                />
                <CatalogSelectRow
                  label="Cabelo"
                  field="hair"
                  value={form.hair}
                  catalog={HAIR}
                  onChange={setCatalog}
                  onCustomChange={setLocalized}
                  placeholder="Selecionar cor de cabelo…"
                  testId="therapist-hair"
                />
                <CatalogSelectRow
                  label="Olhos"
                  field="eyes"
                  value={form.eyes}
                  catalog={EYES}
                  onChange={setCatalog}
                  onCustomChange={setLocalized}
                  placeholder="Selecionar cor de olhos…"
                  testId="therapist-eyes"
                />
              </div>

              {/* Long bio */}
              <div className="rounded-xl border border-white/10 bg-black/20 p-4">
                <p className="mb-3 text-[10px] uppercase tracking-[0.3em] text-gold">
                  Bio detalhada (opcional)
                </p>
                <div className="grid gap-4 md:grid-cols-3">
                  {["pt", "en", "es"].map((lang) => (
                    <div key={lang}>
                      <label className="mb-1 block text-[11px] uppercase tracking-widest text-white/40">
                        {lang.toUpperCase()}
                      </label>
                      <textarea
                        data-testid={`therapist-about-${lang}`}
                        className={ta}
                        value={form.about[lang]}
                        onChange={(e) => setLocalized("about", lang, e.target.value)}
                        placeholder="Descrição livre, uma pequena apresentação da terapeuta…"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ─────────── STEP 2 — Fotos ─────────── */}
          {step === 1 && (
            <>
              <div>
                <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">
                  Foto de Perfil
                </label>
                <ImageUpload
                  value={form.profile}
                  onChange={(url) => set("profile", url)}
                  testId="therapist-profile-upload"
                />
                <p className="mt-2 text-xs text-white/40">
                  Foto principal — aparece nos cartões e na galeria da equipa.
                </p>
              </div>

              <div>
                <label className="mb-2 block text-xs uppercase tracking-[0.2em] text-white/50">
                  Galeria de Fotos ({form.gallery.length})
                </label>
                <MultiImageUpload
                  values={form.gallery}
                  onChange={(urls) => set("gallery", urls)}
                  testId="therapist-gallery-upload"
                />
                <p className="mt-2 text-xs text-white/40">
                  Arraste ficheiros do seu computador para carregar. Arraste as miniaturas para reordenar.
                </p>
              </div>
            </>
          )}

          {/* ─────────── STEP 3 — Massagens + Preview ─────────── */}
          {step === 2 && (
            <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
              <div className="space-y-5">
                <div>
                  <label className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-white/50">
                    Massagens
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {massages.map((m) => (
                      <button
                        key={m.slug}
                        type="button"
                        data-testid={`therapist-massage-toggle-${m.slug}`}
                        onClick={() => toggleMassage(m.slug)}
                        className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                          form.massages.includes(m.slug)
                            ? "border-gold bg-gold/10 text-gold"
                            : "border-white/15 text-white/60 hover:border-white/40"
                        }`}
                      >
                        {m.name?.pt || m.slug}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:gap-8">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-white/70">
                    <input
                      data-testid="therapist-available-checkbox"
                      type="checkbox"
                      checked={form.available_today}
                      onChange={(e) => set("available_today", e.target.checked)}
                      className="h-4 w-4 accent-[#D4AF37]"
                    />
                    Disponível hoje (selo dourado no site)
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-white/70">
                    <input
                      data-testid="therapist-active-checkbox"
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => set("active", e.target.checked)}
                      className="h-4 w-4 accent-[#D4AF37]"
                    />
                    Ativa (visível no site)
                  </label>
                </div>
              </div>

              <div>
                <p className="mb-2 text-[10px] uppercase tracking-[0.3em] text-gold">
                  Pré-visualização
                </p>
                <CardPreview form={form} massages={massages} />
              </div>
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-white/15 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-white"
          >
            Cancelar
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <button
                type="button"
                data-testid="therapist-back"
                onClick={() => setStep((s) => s - 1)}
                className="flex items-center gap-1.5 rounded-full border border-white/15 px-5 py-2 text-xs uppercase tracking-[0.2em] text-white/70 transition-colors hover:border-gold hover:text-gold"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Anterior
              </button>
            )}
            {step < STEPS.length - 1 && (
              <button
                type="button"
                data-testid="therapist-next"
                disabled={!canProceed}
                onClick={() => setStep((s) => s + 1)}
                className="flex items-center gap-1.5 rounded-full bg-gold px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-40"
              >
                Seguinte
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
            {step === STEPS.length - 1 && (
              <button
                data-testid="therapist-save-button"
                type="button"
                onClick={save}
                disabled={busy}
                className="flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-light disabled:opacity-50"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Guardar
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Row for a catalog-driven attribute (nationality / hair / eyes).
 * The operator only picks the PT value; EN/ES are auto-derived from the catalog.
 * If the current pt value isn't in the catalog (legacy data or a rare case), a
 * "Personalizado" mode is offered so nothing is lost and the operator can edit
 * all 3 languages by hand.
 */
const CatalogSelectRow = ({ label, field, value, catalog, onChange, onCustomChange, placeholder, testId }) => {
  const ptValue = value?.pt || "";
  const inCatalog = !ptValue || isInCatalog(catalog, ptValue);
  const [customMode, setCustomMode] = useState(!inCatalog);

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-white/50">{label}</span>
        <button
          type="button"
          data-testid={`${testId}-toggle-custom`}
          onClick={() => setCustomMode((v) => !v)}
          className="text-[10px] uppercase tracking-widest text-white/40 transition-colors hover:text-gold"
        >
          {customMode ? "Usar catálogo" : "Personalizar"}
        </button>
      </div>

      {!customMode ? (
        <>
          <select
            data-testid={`${testId}-select`}
            className={`${smallInp} mt-1.5 appearance-none`}
            value={ptValue}
            onChange={(e) => onChange(field, e.target.value)}
          >
            <option value="">{placeholder}</option>
            {catalog.map((entry) => (
              <option key={entry.pt} value={entry.pt}>
                {entry.pt}
              </option>
            ))}
          </select>
          {ptValue && (
            <p className="mt-1.5 text-[10px] text-white/40" data-testid={`${testId}-preview`}>
              <span className="text-white/60">EN:</span> {value?.en || "—"}{" "}
              <span className="ml-2 text-white/60">ES:</span> {value?.es || "—"}
            </p>
          )}
        </>
      ) : (
        <div className="mt-1.5 grid gap-2 sm:grid-cols-3">
          {["pt", "en", "es"].map((lang) => (
            <input
              key={lang}
              data-testid={`${testId}-${lang}`}
              className={smallInp}
              value={value?.[lang] || ""}
              onChange={(e) => onCustomChange(field, lang, e.target.value)}
              placeholder={lang.toUpperCase()}
            />
          ))}
        </div>
      )}
    </div>
  );
};
